import React, { useState } from "react";
import { Box, Paper, Button, Snackbar, Alert } from "@mui/material";
import { useNavigate } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SidebarMenu from "./AdminPanel/SidebarMenu";
import TopBar from "./AdminPanel/TopBar";
import ProductManager from "./ProductForm/ProductManager";
import AddProduct from "./ProductForm/AddProduct";
import ImagenProduct from "./ProductForm/ImagenProduct";

function getCSRFToken() {
  const name = "csrftoken";
  const cookies = document.cookie.split(";");
  for (let cookie of cookies) {
    const trimmed = cookie.trim();
    if (trimmed.startsWith(name + "=")) {
      return decodeURIComponent(trimmed.substring(name.length + 1));
    }
  }
  return null;
}

const Product = () => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [submenuOpen, setSubmenuOpen] = useState(false);

  const [managerData, setManagerData] = useState({
    section: "",
    category: "",
    brand: "",
    section_seq: "",
    category_seq: "",
    brand_seq: "",
    code: ""
  });

  const [productData, setProductData] = useState({
    code: "",
    name: "",
    barcode: "",
    codigo_auxiliar: "",
    seriado: false,
    pvp1: "",
    estado: true,
    ecommerce: true,
    description: "",
    stock: "",
    stock_minimo: ""
  });

  const [images, setImages] = useState([]);
  const [snackbar, setSnackbar] = useState({
    open: false,
    type: "error",
    message: ""
  });

  const handleLogout = async () => {
    try {
      await fetch("/sistema/logout/", {
        method: "POST",
        credentials: "include"
      });
    } catch {}
    localStorage.clear();
    navigate("/login");
  };

  const handleSelect = (component) => {
    if (component === "AdminSection") navigate("/admin-panel/secciones");
    else if (component === "ModalInventario") setSubmenuOpen((s) => !s);
    else if (component === "AdminProduct") navigate("/admin-panel/inventario/productos");
    else if (component === "AdminVariant") navigate("/admin-panel/inventario/variantes");
    else if (component === "AdminProforma") navigate("/admin-panel/proformas");
    else if (component === "AdminClient") navigate("/admin-panel/clientes");
    else if (component === "AdminServices") navigate("/admin-panel/servicios");
    else if (component === "AdminPromotion") navigate("/admin-panel/promociones");
    else navigate("/admin-panel");
  };

  const handleSubmit = async () => {
    const data = { ...managerData, ...productData };
    const formData = new FormData();

    formData.append("code", String(data.code || "").trim());
    formData.append("name", String(data.name || "").trim());
    formData.append("section", String(data.section || "").trim());
    formData.append("category", String(data.category || "").trim());
    formData.append("brand", String(data.brand || "").trim());
    formData.append("pvp1", String(data.pvp1 || "0").trim());

    formData.append("barcode", String(data.barcode || "").trim());
    formData.append("codigo_auxiliar", String(data.codigo_auxiliar || "").trim());
    formData.append("seriado", data.seriado ? "true" : "false");
    formData.append("stock", String(data.stock || "0").trim());

    formData.append("stock_minimo", String(data.stock_minimo || "0").trim());
    formData.append("estado", data.estado ? "activo" : "inactivo");
    formData.append("ecommerce", data.ecommerce ? "true" : "false");
    formData.append("description", String(data.description || "").trim());

    if (images && images.length > 0) {
      images.forEach((file) => {
        if (file instanceof File) formData.append("images", file, file.name);
      });
    }

    const csrf = getCSRFToken();

    try {
      const response = await fetch("/productos/crear-producto/", {
        method: "POST",
        credentials: "include",
        headers: csrf ? { "X-CSRFToken": csrf } : undefined,
        body: formData
      });

      if (response.ok) {
        setManagerData({
          section: "",
          category: "",
          brand: "",
          section_seq: "",
          category_seq: "",
          brand_seq: "",
          code: ""
        });

        setProductData({
          code: "",
          name: "",
          barcode: "",
          codigo_auxiliar: "",
          seriado: false,
          pvp1: "",
          estado: true,
          ecommerce: true,
          description: "",
          stock: "",
          stock_minimo: ""
        });

        setImages([]);

        navigate("/admin-panel/inventario/productos", {
          state: {
            snackbar: {
              type: "success",
              message: "Producto creado exitosamente"
            }
          },
          replace: true
        });
      } else {
        setSnackbar({
          open: true,
          type: "error",
          message: "Error al crear el producto"
        });
      }
    } catch {
      setSnackbar({
        open: true,
        type: "error",
        message: "Error de conexión con el servidor"
      });
    }
  };

  return (
    <Box sx={{ display: "flex", minHeight: "100vh", backgroundColor: "#edf0f7ff" }}>
      <SidebarMenu
        open={open}
        setOpen={setOpen}
        submenuOpen={submenuOpen}
        setSubmenuOpen={setSubmenuOpen}
        handleSelect={handleSelect}
        handleLogout={handleLogout}
        navigate={navigate}
      />

      <Box sx={{ flexGrow: 1 }}>
        <TopBar open={open} setOpen={setOpen} />
        <Box sx={{ width: "95%", mx: "auto", mt: 3 }}>
          <Button
            variant="contained"
            startIcon={<ArrowBackIcon />}
            sx={{
              textTransform: "none",
              bgcolor: "#1565C0",
              "&:hover": { bgcolor: "#0D47A1" },
              mb: 2
            }}
            onClick={() => navigate(-1)}
          >
            Regresar
          </Button>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "2fr 1fr" },
              gridTemplateAreas: {
                xs: `"product" "contifico" "imagen"`,
                md: `"product imagen" "contifico imagen"`
              },
              gap: 3
            }}
          >
            <Paper elevation={4} sx={{ p: 4, borderRadius: 3, gridArea: "product" }}>
              <ProductManager onProductDataChange={setManagerData} />
            </Paper>

            <Paper elevation={4} sx={{ p: 4, borderRadius: 3, gridArea: "contifico" }}>
              <AddProduct
                onProductDetailsChange={setProductData}
                sectionSeq={managerData.section_seq}
                categorySeq={managerData.category_seq}
                brandSeq={managerData.brand_seq}
                autoCode={managerData.code}
              />
            </Paper>

            <Paper elevation={4} sx={{ p: 4, borderRadius: 3, gridArea: "imagen", gridRow: { md: "span 2" } }}>
              <ImagenProduct value={images} onImagesChange={setImages} />
            </Paper>

            <Box sx={{ gridColumn: "1 / -1", textAlign: "center", mt: 2 }}>
              <Button
                variant="contained"
                sx={{
                  textTransform: "none",
                  bgcolor: "#1565C0",
                  "&:hover": { bgcolor: "#0D47A1" },
                  mb: 3
                }}
                onClick={handleSubmit}
              >
                Crear Producto
              </Button>
            </Box>
          </Box>
        </Box>
      </Box>

      <Snackbar
        open={snackbar.open}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        autoHideDuration={3000}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.type}
          variant="standard"
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Product;
